/* eslint-disable react/no-array-index-key */
'use client';

import Image from 'next/image';
import { Copy } from 'phosphor-icons';
import { useState } from 'react';
import { useBreakpoints } from '../../../lib/hooks/useBreakpoints';
import { c } from '../../../utils/tailwind-merge';
import Button from '../../atoms/Button';
import { BackgroundVideo } from '../../atoms/Video/BackgroundVideo';
import { SnackBar } from '../../molecules/SnackBar';
import { HomepageHeroEmbed } from '../HomepageHero/HomepageHeroEmbed';
import {
  HeroBannerProps,
  HeroBannerPropsCardData,
  HeroBannerPropsVariant,
} from './HeroBanner.types';

const getVariant = ({
  alignment,
  isMobile,
}: {
  alignment: HeroBannerProps['alignment'];
  isMobile: boolean;
}): HeroBannerPropsVariant => {
  if (alignment === 'left' || alignment === 'right') {
    return 'standard';
  } else if (alignment === 'bottom') {
    return 'duo';
  } else if (isMobile) {
    return 'standard';
  }

  return 'takeover';
};

const HeroBannerVisual = ({
  variant,
  image,
  video,
  embeddedVideo,
  hasCookieConsent,
}: Pick<
  HeroBannerPropsCardData,
  'image' | 'video' | 'embeddedVideo' | 'hasCookieConsent'
> & { variant: HeroBannerPropsVariant }) => {
  if (embeddedVideo) {
    return (
      <div
        className={c(
          variant === 'standard' &&
            'relative aspect-video overflow-hidden rounded-xl',
          variant === 'duo' &&
            'relative aspect-[21/9] overflow-hidden rounded-md',
          variant === 'takeover' && 'md:h-[469px] lg:h-[646px]'
        )}
      >
        <HomepageHeroEmbed
          hasCookieConsent={hasCookieConsent ?? false}
          video={embeddedVideo}
          className={c(
            variant === 'takeover' &&
              'absolute inset-0 overflow-hidden rounded-2xl'
          )}
        />
      </div>
    );
  }

  if (video) {
    return (
      <div
        className={c(
          '',
          variant === 'standard' &&
            'relative aspect-video overflow-hidden rounded-xl',
          variant === 'duo' &&
            'relative aspect-[21/9] overflow-hidden rounded-md',
          variant === 'takeover' && 'md:h-[469px] lg:h-[646px]'
        )}
      >
        <BackgroundVideo
          title={video.title}
          fallbackImage={video.fallbackImage}
          url={video.url}
          description={video.description}
          objectFit="cover"
          className={c(
            variant === 'takeover' &&
              'absolute inset-0 overflow-hidden rounded-2xl'
          )}
        />
      </div>
    );
  }

  if (image) {
    return (
      <div
        className={c(
          '',
          variant === 'standard' && 'relative aspect-3/2',
          variant === 'duo' && 'relative aspect-[21/9]',
          variant === 'takeover' && 'md:h-[469px] lg:h-[646px]'
        )}
      >
        <Image
          src={image?.url}
          alt={image?.alt || ''}
          fill
          className={c(
            'object-cover',
            variant === 'standard' && 'rounded-xl',
            variant === 'duo' && 'rounded-md',
            variant === 'takeover' && 'rounded-2xl'
          )}
        />
      </div>
    );
  }

  return null;
};

const HeroBannerContent = ({
  microcopy,
  theme,
  variant,
  subHeader,
  title,
  description,
  countdownDuration,
  countdownType,
  coupon,
  actions,
  onCtaClick,
}: HeroBannerPropsCardData &
  Pick<HeroBannerProps, 'microcopy' | 'theme' | 'onCtaClick'> & {
    variant: HeroBannerPropsVariant;
  }) => {
  const [snackbar, setSnackbar] = useState<boolean>(false);

  return (
    <div
      className={c(
        'flex flex-col items-start gap-5 self-stretch rounded-xl md:max-w-full',
        (variant === 'standard' || variant === 'takeover') &&
          'lg:justify-center',
        variant === 'duo' && 'lg:h-full',
        variant === 'standard' && 'lg:p-8',
        variant === 'takeover' && 'z-10 md:p-4 md:pt-8 lg:p-8 lg:pt-16',
        variant === 'takeover' && theme === 'light' && 'bg-white',
        variant === 'takeover' && theme === 'dark' && 'bg-grey-900'
      )}
    >
      <div
        className={c(
          'flex max-w-[400px] flex-col gap-2.5 self-stretch md:max-w-full lg:gap-3',
          (variant === 'standard' || variant === 'takeover') && 'lg:w-[400px]',
          variant === 'duo' && 'lg:w-full'
        )}
      >
        {subHeader && (
          <p
            className={c(
              'text-xs font-semibold uppercase leading-5 tracking-[1.92px] lg:text-sm lg:tracking-[1.4px]',
              theme === 'light' ? 'text-grey-900' : 'text-white'
            )}
          >
            {subHeader}
          </p>
        )}
        <h2
          className={c(
            'text-3xl font-medium leading-8 tracking-[-0.168px] lg:text-5xl lg:leading-[38px] lg:tracking-[-0.204px]',
            theme === 'light' ? 'text-grey-900' : 'text-white'
          )}
        >
          {title}
        </h2>
        <p
          className={c(
            'leading-[22px] tracking-[-0.096px]',
            theme === 'light' ? 'text-grey-600' : 'text-grey-400'
          )}
        >
          {description}
        </p>
      </div>

      {countdownDuration && (
        <div
          className={c(
            'flex items-center gap-3 rounded-md py-2 pl-4 pr-3 text-sm',
            theme === 'light' ? 'bg-grey-200' : 'bg-grey-800'
          )}
        >
          <div
            className={c(
              'leading-5 tracking-[-0.028px]',
              theme === 'light' ? 'text-grey-700' : 'text-grey-300'
            )}
          >
            {countdownType === 'sale-starts'
              ? microcopy.salesStart
              : microcopy.salesEnd}
          </div>
          <div
            className={c(
              'font-semibold leading-5 tracking-[2.52px]',
              theme === 'light' ? 'text-red-600' : 'text-red-400'
            )}
          >
            {(['days', 'hours', 'minutes', 'seconds'] as const)
              .map((unit) => {
                const value = countdownDuration[unit];

                return value !== undefined
                  ? `${value}${microcopy[unit]}`
                  : undefined;
              })
              .filter((value): value is string => value !== undefined)
              .join(':')}
          </div>
        </div>
      )}

      {coupon ? (
        <div className="flex">
          <button
            className={c(
              'flex items-center gap-2 rounded-md py-2 pl-4 pr-3 text-sm',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-hover',
              theme === 'light'
                ? 'bg-grey-200 hover:bg-grey-300/50'
                : 'bg-grey-700 hover:bg-grey-600/50'
            )}
            type="button"
            aria-label={microcopy.copyCouponCode}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(coupon);
              } catch {
                // Fallback for older browsers
                const textarea = document.createElement('textarea');
                textarea.value = coupon;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
              } finally {
                setSnackbar(true);
              }
            }}
          >
            <div
              className={c(
                'text-sm uppercase leading-5 tracking-[2.52px]',
                theme === 'light' ? 'text-grey-700' : 'text-grey-300'
              )}
            >
              {coupon}
            </div>
            <Copy
              className={c(
                'h-5 w-5',
                theme === 'light' ? 'text-grey-900' : 'text-grey-100'
              )}
            />
          </button>
          {snackbar ? (
            <SnackBar
              icon="Copy"
              iconWeight="bold"
              closeIcon="X"
              duration={800}
              microcopy={{
                title: microcopy.couponCodeCopied,
                closeAriaLabel: microcopy.close,
              }}
              onClose={() => {
                setSnackbar(false);
              }}
            />
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}

      <div className={c('flex gap-3', variant === 'duo' && 'lg:mt-auto')}>
        {actions.map((action) => (
          <Button.v2
            key={action.label}
            variant={
              action.style === 'primary'
                ? 'primary'
                : theme === 'light'
                  ? 'tertiary-black-monochrome'
                  : 'tertiary'
            }
            size="small"
            asChild
            onClick={() => onCtaClick(action.label)}
          >
            <a
              href={action.url}
              target={action.openInNewTab ? '_blank' : undefined}
              rel={action.openInNewTab ? 'noopener noreferrer' : undefined}
            >
              {action.label}
            </a>
          </Button.v2>
        ))}
      </div>
    </div>
  );
};

export const HeroBannerCard = ({
  microcopy,
  subHeader,
  title,
  description,
  image,
  video,
  embeddedVideo,
  hasCookieConsent,
  countdownDuration,
  countdownType,
  coupon,
  actions,
  theme,
  alignment,
  variant,
  onCtaClick,
}: HeroBannerPropsCardData &
  Pick<HeroBannerProps, 'microcopy' | 'theme' | 'alignment' | 'onCtaClick'> & {
    variant: HeroBannerPropsVariant;
  }) => {
  return (
    <div
      className={c(
        'relative flex w-full items-start justify-center rounded-lg',
        variant === 'takeover'
          ? 'bg-transparent'
          : theme === 'light'
            ? 'bg-grey-100'
            : 'bg-grey-900',
        variant === 'standard' &&
          'flex-col gap-6 px-4 pb-4 pt-12 lg:max-w-[1228px] lg:items-center lg:justify-between lg:rounded-lg',
        variant === 'standard' && alignment === 'left' && 'lg:flex-row lg:p-0',
        variant === 'standard' &&
          alignment === 'right' &&
          'lg:flex-row-reverse lg:py-0 lg:pl-0 lg:pr-12',
        variant === 'duo' && 'flex-col-reverse gap-8 p-4 lg:justify-between'
      )}
    >
      <div
        className={c(
          'flex flex-col items-start gap-5 self-stretch md:max-w-full',
          variant === 'standard' && 'lg:justify-center',
          variant === 'duo' && 'lg:h-full',
          variant === 'takeover' &&
            alignment === 'bottom-left' &&
            'md:justify-end md:p-4',
          variant === 'takeover' &&
            alignment === 'top-left' &&
            'md:justify-start md:p-4'
        )}
      >
        <HeroBannerContent
          theme={theme}
          variant={variant}
          microcopy={microcopy}
          subHeader={subHeader}
          title={title}
          description={description}
          countdownDuration={countdownDuration}
          countdownType={countdownType}
          coupon={coupon}
          actions={actions}
          onCtaClick={onCtaClick}
        />
      </div>

      <div className={c('w-full', alignment !== 'bottom' && 'lg:p-4')}>
        <HeroBannerVisual
          variant={variant}
          image={image}
          video={video}
          embeddedVideo={embeddedVideo}
          hasCookieConsent={hasCookieConsent}
        />
      </div>
    </div>
  );
};

export const HeroBanner = ({
  cards,
  microcopy,
  theme,
  alignment,
  className = '',
  onCtaClick,
}: HeroBannerProps) => {
  const breakpoints = useBreakpoints();
  const variant: HeroBannerPropsVariant = getVariant({
    alignment,
    isMobile: breakpoints?.isMobile ?? false,
  });

  return (
    <div
      className={c(
        'group flex flex-col gap-4 xl:container',
        variant === 'duo' && 'lg:flex-row',
        className
      )}
    >
      {cards.map((card, index) => (
        <HeroBannerCard
          key={`${card.title}-${index}`}
          microcopy={microcopy}
          theme={theme}
          alignment={alignment}
          variant={variant}
          onCtaClick={onCtaClick}
          {...card}
        />
      ))}
    </div>
  );
};
